/**
 * PDF取込（AIブロック骨組み自動生成）用: 1ページ目のテキスト層を座標付きで抽出する。
 * born-digital な PDF（現行Excel出力・会計ソフト出力等）ではテキストと pt 座標が
 * 無料の正解データとして取れるため、OCR不要でAIへ渡せる（pdf-template-import-ai.md §3）。
 *
 * PNG/JPEG や、テキスト層を持たないスキャンPDFはこの関数を呼ばない
 * （呼び出し側が text_items: [] を直接組み立てる）。
 *
 * pdfjs-dist は fileToBackgroundImage.ts と同様に動的 import で必要時のみ読み込む。
 */
export type PdfTextItem = {
  text: string;
  x_mm: number;
  y_mm: number;
  width_mm: number;
  font_size_pt: number;
};

export type PdfPageInfo = {
  width_mm: number;
  height_mm: number;
  orientation: "portrait" | "landscape";
  text_items: PdfTextItem[];
};

/** プロンプト肥大を防ぐための上限。超過分は y_mm 昇順（ページ上部優先）で切り捨てる */
const MAX_TEXT_ITEMS = 500;

const PT_TO_MM = 25.4 / 72;

function round1(value: number): number {
  return Math.round(value * 10) / 10;
}

export async function extractPdfTextItems(file: File): Promise<PdfPageInfo> {
  const pdfjs = await import("pdfjs-dist");
  const workerUrl = (await import("pdfjs-dist/build/pdf.worker.min.mjs?url")).default;
  pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;

  const loadingTask = pdfjs.getDocument({ data: await file.arrayBuffer() });
  try {
    const doc = await loadingTask.promise;
    const page = await doc.getPage(1);
    const viewport = page.getViewport({ scale: 1 });
    const pageWidthPt = viewport.width;
    const pageHeightPt = viewport.height;
    const widthMm = round1(pageWidthPt * PT_TO_MM);
    const heightMm = round1(pageHeightPt * PT_TO_MM);
    const orientation: "portrait" | "landscape" = widthMm > heightMm ? "landscape" : "portrait";

    const textContent = await page.getTextContent();
    const items: PdfTextItem[] = [];
    for (const raw of textContent.items) {
      const item = raw as { str?: string; transform?: number[]; width?: number };
      const text = item.str ?? "";
      if (!text.trim()) continue; // 空白のみの断片は除外

      const transform = item.transform ?? [1, 0, 0, 1, 0, 0];
      const xPt = transform[4] ?? 0;
      const yPt = transform[5] ?? 0;
      // PDFは左下原点 → 左上原点へ変換
      const xMm = xPt * PT_TO_MM;
      const yMm = (pageHeightPt - yPt) * PT_TO_MM;
      const widthMmItem = (item.width ?? 0) * PT_TO_MM;
      // 回転を考慮した近似（transform[2], transform[3] のノルム）で十分
      const fontSizePt = Math.hypot(transform[2] ?? 0, transform[3] ?? 0);

      items.push({
        text,
        x_mm: round1(xMm),
        y_mm: round1(yMm),
        width_mm: round1(widthMmItem),
        font_size_pt: round1(fontSizePt),
      });
    }

    // 個数上限を超える場合は y_mm 昇順（ページ上部優先）で切り捨てる
    items.sort((a, b) => a.y_mm - b.y_mm);
    const textItems = items.length > MAX_TEXT_ITEMS ? items.slice(0, MAX_TEXT_ITEMS) : items;

    return { width_mm: widthMm, height_mm: heightMm, orientation, text_items: textItems };
  } finally {
    await loadingTask.destroy();
  }
}
